/**
 * Background Media Section JavaScript
 * Version optimisée avec noms courts
 */

class BgMedia {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.parallaxEnabled = container.dataset.parallax === 'true';
    this.textElement = container.querySelector('.bg-media__text');
    this.imageElement = container.querySelector('.bg-media__image');
    this.parallaxContainer = container.querySelector('.parallax-container');
    this.parallaxImage = container.querySelector('.parallax-image');
    
    this.init();
  }

  init() {
    // Initialiser le défilement de parallaxe
    if (this.parallaxEnabled && this.parallaxImage) {
      this.setupParallax();
    }
    
    // Optimisation des images
    if (this.imageElement) {
      this.setupImageOptimization();
    }
    
    // Animation au défilement
    this.setupScrollAnimation();
    
    // Ajouter des écouteurs d'événements
    this.addEventListeners();
    
    // Marquer comme initialisé
    this.container.classList.add('is-initialized');
  }

  setupParallax() {
    // Utilisation d'IntersectionObserver pour des performances optimisées
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.handleParallaxScroll.bind(this));
          observer.unobserve(this.parallaxContainer);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(this.parallaxContainer);
  }

  handleParallaxScroll() {
    if (!this.parallaxImage) return;
    
    const scrollPosition = window.pageYOffset;
    const containerRect = this.container.getBoundingClientRect();
    const containerTop = containerRect.top + scrollPosition;
    const containerHeight = containerRect.height;
    
    // Calculer le déplacement en parallaxe uniquement si la section est visible
    if (scrollPosition + window.innerHeight > containerTop && 
        scrollPosition < containerTop + containerHeight) {
      
      const movement = this.parallaxImage.dataset.movement || '15%';
      const movementValue = parseInt(movement) / 100;
      const parallaxDirection = this.parallaxImage.dataset.angle || 'top';
      
      // Calculer la position relative dans la vue
      const relativePosition = (scrollPosition + window.innerHeight - containerTop) / 
                              (containerHeight + window.innerHeight);
      
      // Appliquer le déplacement en fonction de la direction
      if (parallaxDirection === 'top') {
        const yPos = (relativePosition * movementValue * containerHeight);
        this.parallaxImage.style.transform = `translate3d(0, ${yPos}px, 0)`;
      } else {
        const xPos = (relativePosition * movementValue * containerRect.width);
        this.parallaxImage.style.transform = `translate3d(${xPos}px, 0, 0)`;
      }
    }
  }

  setupImageOptimization() {
    // S'assurer que les images sont chargées efficacement
    if ('loading' in HTMLImageElement.prototype) {
      const img = this.imageElement.querySelector('img');
      if (img && !img.hasAttribute('loading')) {
        img.loading = 'lazy';
      }
    }
    
    // Assurer une meilleure qualité d'image sur les écrans haute résolution
    if (window.devicePixelRatio > 1) {
      const img = this.imageElement.querySelector('img');
      if (img && img.srcset === '') {
        const originalSrc = img.src;
        const extension = originalSrc.split('.').pop();
        const srcWithoutExtension = originalSrc.replace('.' + extension, '');
        
        if (window.devicePixelRatio > 2) {
          img.srcset = `${srcWithoutExtension}_2x.${extension} 2x, ${srcWithoutExtension}_3x.${extension} 3x`;
        } else {
          img.srcset = `${srcWithoutExtension}_2x.${extension} 2x`;
        }
      }
    }
  }

  setupScrollAnimation() {
    // Animation au défilement avec IntersectionObserver pour de meilleures performances
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('has-animation');
          // Une fois animé, arrêter d'observer
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    
    observer.observe(this.container);
  }

  addEventListeners() {
    // Optimisation pour le redimensionnement de fenêtre
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Recalculer les dimensions pour le parallaxe
        if (this.parallaxEnabled && this.parallaxImage) {
          this.handleParallaxScroll();
        }
      }, 150);
    });
    
    // Ajouter des effets de survol pour améliorer l'UX
    if (this.textElement) {
      this.container.addEventListener('mouseenter', () => {
        this.textElement.classList.add('is-hovered');
      });
      
      this.container.addEventListener('mouseleave', () => {
        this.textElement.classList.remove('is-hovered');
      });
    }
  }

  onUnload() {
    // Nettoyer les écouteurs d'événements lors du déchargement de la section
    window.removeEventListener('scroll', this.handleParallaxScroll.bind(this));
    window.removeEventListener('resize', this.handleResizeEvent);
  }
}

// Initialiser toutes les sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="bg-img"]');
  sections.forEach(section => {
    new BgMedia(section);
  });
  
  // Support pour l'éditeur Shopify - initialiser les nouvelles sections ajoutées
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId && 
        event.detail.section.container.dataset.sectionType === 'bg-img') {
      new BgMedia(event.detail.section.container);
    }
  });
});